import { sendNotification, useNavigation, useRouteParams, useTranslation } from '@msdk/common'
import { CustomActionPosition, Entity, OpenMode, ViewColumnDetail } from '@msdk/definitions'
import {
  BodyWrapper,
  ButtonScheleton,
  CardTable,
  createComponentWrapperWithRef,
  FormControlLabel,
  Pagination,
  styled,
  Switch,
  TableWrapper,
} from '@msdk/ui'
import React, { useEffect, useState } from 'react'

import { useFormModal, useView, useViewActionButtons } from '../../hooks'
import { DynamicView } from '../DynamicView'
import { DynamicViewProvider } from '../DynamicView/context'
import { Filters } from '../Filters'
import { ViewActions } from '../ViewActions'
import { useViewContext } from '../ViewContextProvider'

interface Props {
  entityName: string
  moduleName: string
}

const MuiHeader = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 8px 8px 0 0;
  flex-wrap: wrap;
  gap: 8px;

  padding: 16px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #fff;
`

const NovaHeader = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 8px 8px 0 0;
  flex-wrap: wrap;
  gap: 8px;
`

const Header = createComponentWrapperWithRef(
  {
    mui: MuiHeader,
    nova: NovaHeader,
  },
  'Header',
)

const ActionsContainer = styled('div')`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
`

export function PageView({ entityName, moduleName }: Props) {
  const navigate = useNavigation()
  const t = useTranslation()
  const { itemId } = useRouteParams()

  const viewData = useView(entityName as string, moduleName as string)
  const { createModal } = useFormModal()

  const {
    entityModel,
    viewModel,
    isLoading,
    response,
    currentPage,
    filterChange,
    clearAllFilters,
    onPageChange,
    onOrderChange,
    reload,
    acl,
    selectedRows,
    onRowSelected,
    currentFilters,
    initialFilters,
    isMultiselected,
    setIsMultiselect,
    addColumnFilter,
    removeColumnFilter,
    removeAllColumnFilter,
    additionalCondition,
    isInitialized,
    currentOrder,
    addOrUpdateFilter,
  } = viewData

  const viewContext = useViewContext()

  const onRowClicked = (row: Entity) => {
    const form = viewModel.openFormModel ?? entityModel.defaultForm
    const ctx: Record<string, string> = { [row.entityName]: row.id }
    const qs = new URLSearchParams()
    qs.append('mode', 'Readonly')

    if (itemId) {
      // Add the current sitemap item id to the context so that
      // when we go back from the form we can find the correct item
      // to go back to
      ctx['sId'] = itemId
    }

    if (!form) {
      sendNotification({
        message:
          'Missing DefaultForm in entity model and no OpenFormModel was specified in the view, can not determine the form to open!',
        status: 'error',
      })
    } else if (viewModel.openMode !== OpenMode.Modal) {
      navigate(`${entityModel.name}/${form}/${row.id}`, viewModel.associatedSiteMap, ctx, qs)
    } else {
      const modal = createModal(
        row.entityName,
        form,
        row.id,
        (entity: Entity) => {
          if (entity) {
            reload()
          }
        },
        {
          modalForm: viewModel.modalForm,
        },
      )
      modal.open()
    }
  }

  useEffect(() => {
    const newCtx = {
      isLoading,
      response,
      acl,
      reload,
      selectedRows,
      currentFilters,
      isInitialized,
    }
    viewContext.update(newCtx)
  }, [isLoading, response, acl, reload, selectedRows, currentFilters, isInitialized])

  const customActions = useViewActionButtons(
    viewModel,
    CustomActionPosition.InsideView,
    reload,
    viewContext.context.selectedRows,
    viewContext.context.acl,
  )

  const [showExtra, setShowExtra] = useState<boolean>(false)

  const shouldShowExtraToggle = viewModel.columns.some(
    (column) => !column.hidden && column.detail === ViewColumnDetail.Extra,
  )

  function toggleShowExtra() {
    setShowExtra((prev) => !prev)
  }

  return (
    <BodyWrapper>
      <CardTable>
        <Header>
          <Filters
            filters={viewModel.filters}
            viewName={viewModel.name}
            onFilterChange={filterChange}
            multiselect={isMultiselected}
            setMultiselect={setIsMultiselect}
            acl={acl}
            entityModel={entityModel}
            initialValues={initialFilters}
          />
          <ActionsContainer>
            {shouldShowExtraToggle && (
              <FormControlLabel
                label={
                  viewModel.extra && viewModel.extra.idml && viewModel.extra.description
                    ? t(viewModel.extra.idml, viewModel.extra.description)
                    : t('LBL_DYNAMIC_VIEW_SHOW_EXTRA', 'Show extra columns')
                }
                control={
                  <Switch
                    checked={showExtra}
                    onChange={toggleShowExtra}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    color="primary"
                  />
                }
              />
            )}
            {!isInitialized && <ButtonScheleton />}
            {isInitialized && (
              <ViewActions
                actions={customActions}
                entityName={viewModel.entity}
                viewName={viewModel.name}
                reload={reload}
                currentFilters={currentFilters}
                acl={acl}
                selectedRows={viewContext.context.selectedRows!}
              />
            )}
          </ActionsContainer>
        </Header>
        <TableWrapper>
          <DynamicViewProvider
            entityModel={entityModel}
            viewModel={viewModel}
            rows={response?.source || []}
            reload={reload}
            isLoading={isLoading}
            onRowClick={onRowClicked}
            onOrderChange={onOrderChange}
            multiSelect={isMultiselected}
            onRowSelected={onRowSelected}
            selectedRows={selectedRows}
            acl={acl}
            addColumnFilter={addColumnFilter}
            removeColumnFilter={removeColumnFilter}
            removeAllColumnFilter={removeAllColumnFilter}
            currentFilters={currentFilters}
            additionalCondition={additionalCondition}
            showExtra={showExtra}
            hideTopBorder={true}
            currentOrder={currentOrder}
            addOrUpdateFilter={addOrUpdateFilter}
            clearAllFilters={clearAllFilters}
          >
            <DynamicView />
          </DynamicViewProvider>
        </TableWrapper>
        {!viewModel.hidePagination && (
          <Pagination
            component="div"
            count={response?.totalItems ?? 0}
            page={currentPage}
            rowsPerPage={response?.pageSize ?? 30}
            rowsPerPageOptions={[]}
            onPageChange={(_e, page) => onPageChange(page)}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t('CMPSR_PAGINATOR_OF', 'of')} ${count}`}
          />
        )}
      </CardTable>
    </BodyWrapper>
  )
}
